// The booking's Therapists, each with the approved state machine's state (0227)
// and — for a Superadmin, on unstarted work only — Replace (0230). Plan S6.
//
// Scope comes from the view: `v_ops_booking_entries` is hub-scoped in SQL and
// carries no client identity, so this card cannot widen the data wall.

import type { BookingEntryState } from "@bednbody/logic";
import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bednbody/ui/components/card";
import { useState } from "react";
import {
  OPS_ENTRY_STATE_LABEL,
  type OpsBookingEntry,
  useOpsBookingEntries,
  useStaffNames,
} from "./booking-entries.hooks";
import { ReplaceTherapistDialog } from "./ReplaceTherapistDialog";

const STATE_VARIANT: Record<BookingEntryState, BadgeVariant> = {
  pending: "warning",
  scheduled: "info",
  finding_rider: "warning",
  searching_rider: "warning",
  reassigning: "warning",
  active: "primary",
  completed: "success",
  client_cancelled: "muted",
  system_cancelled: "muted",
};

function windowLabel(entry: OpsBookingEntry): string {
  if (!entry.windowStartsAt) return "—";
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-PH", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: "Asia/Manila",
    });
  return entry.windowEndsAt
    ? `${fmt(entry.windowStartsAt)}–${fmt(entry.windowEndsAt)}`
    : fmt(entry.windowStartsAt);
}

export function TherapistEntriesCard({
  bookingGroupId,
  isSuperadmin,
}: {
  bookingGroupId: string | null;
  isSuperadmin: boolean;
}) {
  const entries = useOpsBookingEntries(bookingGroupId);
  const list = entries.data ?? [];
  const names = useStaffNames(
    list.map((e) => e.therapistId).filter((id): id is string => id !== null),
  );
  const [replacing, setReplacing] = useState<OpsBookingEntry | null>(null);

  if (!bookingGroupId || list.length === 0) return null;

  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>{list.length > 1 ? "Therapists" : "Therapist"}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {list.map((entry) => {
            const name =
              (entry.therapistId ? names.data?.get(entry.therapistId) : null) ?? "Not assigned";
            // SCHED-014 / 0230: only work nobody has set out on — the Rider is
            // released first otherwise, and the server says so.
            const replaceable =
              isSuperadmin && entry.therapistId !== null && entry.leadStatus === "awaiting_rider";
            return (
              <li
                key={entry.leadBookingId}
                className="flex items-start justify-between gap-3 border-b border-border pb-3 last:border-b-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{name}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.clientCount} {entry.clientCount === 1 ? "client" : "clients"} ·{" "}
                    {windowLabel(entry)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  {entry.state ? (
                    <Badge variant={STATE_VARIANT[entry.state]}>
                      {OPS_ENTRY_STATE_LABEL[entry.state]}
                    </Badge>
                  ) : null}
                  {replaceable ? (
                    <Button size="sm" variant="outline" onClick={() => setReplacing(entry)}>
                      Replace therapist
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
      {replacing?.therapistId ? (
        <ReplaceTherapistDialog
          open
          onOpenChange={(open) => (open ? null : setReplacing(null))}
          bookingGroupId={bookingGroupId}
          hubId={replacing.hubId}
          fromTherapistId={replacing.therapistId}
          fromTherapistName={names.data?.get(replacing.therapistId) ?? "this Therapist"}
        />
      ) : null}
    </Card>
  );
}
